/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React from 'react';
import { object, string } from 'prop-types';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { getFeatureFlags } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import brandThemes from '../../../../AppShell/brandtheme';

const seoOpenGraphImage = (seo) => (seo?.og?.length ? seo.og.find((prop) => prop.property === 'og:image')?.content : null);

const brandConfigurationPrimaryLogo = (brand) => (brandThemes[brand?.code]?.logos?.primary_logo?.url ? brandThemes[brand.code].logos.primary_logo.url : null);

const SeoHPJsonLdSchema = ({ brand, domainURL, seo }) => {
    const logoUrl = seoOpenGraphImage(seo) || brandConfigurationPrimaryLogo(brand) || null;
    const logoClause = logoUrl ? `"logo": "${logoUrl}",` : '';
    const featureFlags = useSelector(getFeatureFlags);
    const celebrationsPath = featureFlags['seo-schema-passport-path-table']?.[brand.domain] || '/passport-flowers';

    return (
        <Helmet>
            (
            <script type="application/ld+json">
                {`{"@context": "http://schema.org","@type": "WebSite","url": "${domainURL}","potentialAction": {"@type": "SearchAction","target": "${domainURL}/searchterm/{search_term_string}","query-input": "required name=search_term_string"}}`}
            </script>
            <script type="application/ld+json">
                {`{
                        "@context":"https://schema.org",
                        "@graph":[
                            { 
                                "@type":"WebSite",
                                "@id":"${domainURL}/#website",
                                "url":"${domainURL}",
                                "name":"${brand.description}",
                                "potentialAction": {
                                    "@type":"SearchAction",
                                    "target":"${domainURL}/searchterm/{search_term_string}",
                                    "query-input":"required name=search_term_string"
                                }
                            },{
                                "@type":"Organization",
                                "@id":"${domainURL}/#organization",
                                "name":"${brand.description}", 
                                ${logoClause}
                                "url":"${domainURL}",
                                "address": {
                                    "@type":"PostalAddress",
                                    "streetAddress":"2750 Commerce Park Drive, Suite 180",
                                    "addressLocality":"Jericho",
                                    "addressRegion":"NY",
                                    "postalCode":"11753"
                                },
                                "contactPoint": {
                                    "@type":"ContactPoint",
                                    "telephone":"+1-888-471-2090",
                                    "contactType":"Customer Service"
                                },
                                "sameAs":["https://www.facebook.com/${brand.domain}/",
                                "https://twitter.com/${brand.domain}",
                                "https://www.instagram.com/${brand.domain}/",
                                "https://www.youtube.com/${brand.domain}",
                                "https://www.linkedin.com/company/${brand.domain}-com",
                                "https://www.pinterest.com/${brand.domain}/"],
                                "hasMemberProgram":{
                                    "@type":"MemberProgram",
                                    "@id":"${domainURL}${celebrationsPath}#celebrations-passport",
                                    "name":"Celebrations Passport",
                                    "url":"${domainURL}${celebrationsPath}",
                                    "description":"Celebrations Passport is an annual paid loyalty program offering free standard shipping and no service charge on eligible gifts across the brand family.",
                                    "hasTiers": [{
                                        "@type": "MemberProgramTier",
                                        "@id": "${domainURL}${celebrationsPath}#celebrations-passport-tier",
                                        "name": "Passport Member",
                                        "hasTierBenefit": [
                                            "https://schema.org/TierBenefitLoyaltyShipping"
                                            ]
                                        }
                                    ]
                                }
                            }
                        ]
                    }`}
            </script>
            )
        </Helmet>
    );
};

SeoHPJsonLdSchema.propTypes = {
    brand: object.isRequired,
    domainURL: string,
    seo: object,
};

SeoHPJsonLdSchema.defaultProps = {
    domainURL: '',
    seo: {},
};

export default SeoHPJsonLdSchema;
